/**
 * The "new studio" dialog: a name, an optional id (the one groups' grants
 * name, D14) and site, and what to start from — the template (`acp
 * studio-template`'s config for the three simulated devices, on a host and
 * port block) or an empty config to fill in on the studio page. The config
 * itself is built by the container from the one template source.
 */
import { entityIdSchema } from '@acp/api-contract/tenancy.js';
import { type FormEvent, useState } from 'react';
import { Button } from '../ui/button.js';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog.js';
import { Field, FieldDescription, FieldError } from '../ui/field.js';
import { Input } from '../ui/input.js';
import { Label } from '../ui/label.js';
import { NativeSelect } from '../ui/native-select.js';

/** An optional id field's error against the contract's id alphabet (`entityIdSchema`, a key segment), or null. */
function idError(text: string): string | null {
  return text.trim() === '' || entityIdSchema.safeParse(text.trim()).success ? null : 'Letters, digits, - and _ only, up to 64.';
}

export type StudioRequest = {
  readonly name: string;
  readonly studioId: string | null;
  readonly siteId: string | null;
  /** Null: an empty config. */
  readonly template: { readonly host: string; readonly basePort: number } | null;
  /** Its default show style (D32): an existing one, a new one made from the show-style template, or none. */
  readonly defaultShowStyle: { readonly kind: 'existing'; readonly showStyleId: string } | { readonly kind: 'template' } | null;
};

export type CreateStudioDialogProps = {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** The simulated devices' defaults: this page's host, the primary checkout's port block. */
  readonly defaultHost: string;
  readonly defaultBasePort: number;
  /** The tenant's show styles, one of which can be the studio's default. */
  readonly showStyles: readonly { readonly id: string; readonly name: string }[];
  readonly pending: boolean;
  readonly error: string | null;
  readonly onSubmit: (request: StudioRequest) => void;
};

/** The default-show-style select's "new from the template" value: a space cannot be in an id. */
const TEMPLATE_CHOICE = ' template';

function portOf(text: string): number | null {
  const n = Number(text);
  return Number.isInteger(n) && n > 0 && n < 65_530 ? n : null;
}

function CreateStudioForm({ onOpenChange, defaultHost, defaultBasePort, showStyles, pending, error, onSubmit }: Omit<CreateStudioDialogProps, 'open'>) {
  const [name, setName] = useState('');
  const [studioId, setStudioId] = useState('');
  const [siteId, setSiteId] = useState('');
  const [fromTemplate, setFromTemplate] = useState(true);
  const [host, setHost] = useState(defaultHost);
  const [basePort, setBasePort] = useState(String(defaultBasePort));
  // '': none; TEMPLATE_CHOICE: a new show style from the template; else an existing id. Shown: the pick while it is still a choice.
  const [pickedShowStyle, setShowStyle] = useState<string | null>(null);
  const shownShowStyle = pickedShowStyle !== null && (pickedShowStyle === '' || pickedShowStyle === TEMPLATE_CHOICE || showStyles.some((s) => s.id === pickedShowStyle)) ? pickedShowStyle : (showStyles[0]?.id ?? (fromTemplate ? TEMPLATE_CHOICE : ''));
  const studioIdError = idError(studioId);
  const siteIdError = idError(siteId);
  const port = portOf(basePort);
  const ready = name.trim() !== '' && studioIdError === null && siteIdError === null && (!fromTemplate || (host.trim() !== '' && port !== null));
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!ready || pending) return;
    onSubmit({
      name: name.trim(),
      studioId: studioId.trim() === '' ? null : studioId.trim(),
      siteId: siteId.trim() === '' ? null : siteId.trim(),
      template: fromTemplate && port !== null ? { host: host.trim(), basePort: port } : null,
      defaultShowStyle: shownShowStyle === '' ? null : shownShowStyle === TEMPLATE_CHOICE ? { kind: 'template' } : { kind: 'existing', showStyleId: shownShowStyle },
    });
  };
  return (
    <form className="grid gap-4" onSubmit={submit}>
      <DialogHeader>
        <DialogTitle>New studio</DialogTitle>
        <DialogDescription>Start from the template — the three simulated devices, mapped to the show-style template’s layers — or from nothing.</DialogDescription>
      </DialogHeader>
      <Field>
        <Label htmlFor="studio-name">Name</Label>
        <Input id="studio-name" value={name} onChange={(e) => setName(e.target.value)} disabled={pending} autoFocus />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field invalid={studioIdError !== null}>
          <Label htmlFor="studio-id">Id (optional)</Label>
          <Input id="studio-id" value={studioId} onChange={(e) => setStudioId(e.target.value)} disabled={pending} aria-invalid={studioIdError !== null} placeholder="studio-a" />
          <FieldError>{studioIdError}</FieldError>
        </Field>
        <Field invalid={siteIdError !== null}>
          <Label htmlFor="studio-site">Site (optional)</Label>
          <Input id="studio-site" value={siteId} onChange={(e) => setSiteId(e.target.value)} disabled={pending} aria-invalid={siteIdError !== null} />
          <FieldError>{siteIdError}</FieldError>
        </Field>
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={fromTemplate} onChange={(e) => setFromTemplate(e.target.checked)} disabled={pending} data-testid="studio-from-template" />
        Start from the template (simulated devices)
      </label>
      {fromTemplate ? (
        <div className="grid grid-cols-2 gap-3">
          <Field>
            <Label htmlFor="studio-sim-host">Simulator host</Label>
            <Input id="studio-sim-host" value={host} onChange={(e) => setHost(e.target.value)} disabled={pending} />
          </Field>
          <Field invalid={port === null}>
            <Label htmlFor="studio-sim-port">Port block</Label>
            <Input id="studio-sim-port" value={basePort} inputMode="numeric" onChange={(e) => setBasePort(e.target.value)} disabled={pending} aria-invalid={port === null} />
            <FieldDescription>The devices listen on base + 5, 6 and 7 (`pnpm ports`).</FieldDescription>
          </Field>
        </div>
      ) : null}
      <Field>
        <Label htmlFor="studio-default-show-style">Default show style</Label>
        <NativeSelect id="studio-default-show-style" value={shownShowStyle} onChange={(e) => setShowStyle(e.target.value)} disabled={pending}>
          <option value="">(none)</option>
          <option value={TEMPLATE_CHOICE}>New from the show-style template</option>
          {showStyles.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </NativeSelect>
        <FieldDescription>What its productions run unless they name another show style (D32). Its devices are mapped to that show style's layers.</FieldDescription>
      </Field>
      <FieldError>{error}</FieldError>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>
          Cancel
        </Button>
        <Button type="submit" disabled={!ready || pending} data-testid="create-studio-submit">
          {pending ? 'Creating…' : 'Create'}
        </Button>
      </DialogFooter>
    </form>
  );
}

export function CreateStudioDialog({ open, ...rest }: CreateStudioDialogProps) {
  return (
    <Dialog open={open} onOpenChange={rest.onOpenChange}>
      <DialogContent data-testid="create-studio-dialog">
        <CreateStudioForm {...rest} />
      </DialogContent>
    </Dialog>
  );
}
